import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { CredentialStrip } from "@/components/CoverPreview";
import { toast } from "sonner";
import { Loader2, Save, Star, ShieldCheck, Sparkles, Trash2, RefreshCw, Database } from "lucide-react";

export default function BrandSettings() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [wiping, setWiping] = useState(false);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-demo");
      toast.success(`Created ${data.count} demo catalogues`);
    } catch (e) {
      toast.error("Could not create demo data");
    } finally {
      setSeeding(false);
    }
  };

  const wipe = async (scope) => {
    setWiping(true);
    try {
      const { data } = await api.post("/admin/wipe-data", { scope });
      toast.success(scope === "demo" ? `Removed ${data.catalogues_removed} demo catalogues` : "All catalogue data wiped");
    } catch (e) {
      toast.error("Could not wipe data");
    } finally {
      setWiping(false);
    }
  };

  useEffect(() => { api.get("/brand").then((r) => setB(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setB((x) => ({ ...x, [k]: v }));

  const setMkt = (i, patch) => {
    const marketplaces = [...b.marketplaces];
    marketplaces[i] = { ...marketplaces[i], ...patch };
    set("marketplaces", marketplaces);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/brand", b);
      setB(data);
      toast.success("Brand settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  if (!b) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#0B3B2E]" /></div>;

  const field = (k, label, ph) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={b[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} data-testid={`brand-${k}`} />
    </div>
  );

  return (
    <div className="pgt-fade-up space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A34A] font-semibold">Compliance & identity</div>
          <h1 className="font-display text-4xl text-[#0B3B2E]">Brand Settings</h1>
        </div>
        <Button onClick={save} disabled={saving} data-testid="brand-save" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </Button>
      </div>

      <Card className="p-6">
        <h3 className="font-display text-2xl text-[#0B3B2E] mb-4">Company Identity</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {field("company", "Company")}
          {field("division", "Division")}
          {field("website", "Website")}
          {field("phone", "Phone")}
          {field("alt_phone", "Alternate phone")}
          {field("whatsapp", "WhatsApp number (chat buttons)", "defaults to Phone")}
          {field("catalogue_number_prefix", "Catalogue number prefix", "PGT-CAT")}
          <div className="sm:col-span-2">{field("address", "Address")}</div>
        </div>
        <Separator className="my-5" />
        <div className="grid sm:grid-cols-2 gap-6 items-start">
          <FileUpload
            label="Official Prime Gold Tea logo (transparent PNG preferred)"
            accept=".png,.jpg,.jpeg,.webp" purpose="logo" testid="brand-logo" heightClass="h-40"
            value={b.logo_file_id ? { id: b.logo_file_id, url: b.logo_url } : null}
            onChange={(f) => setB((x) => ({ ...x, logo_file_id: f?.id || null, logo_url: f?.url || null }))}
          />
          <div className="rounded-lg bg-[#FAF7F0] border border-[#C9A34A]/25 p-3 text-xs text-[#14513D]/80">
            Use the authentic Prime Gold Tea logo only. Do not recreate or restyle it.
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-display text-2xl text-[#0B3B2E] mb-1">Tea Board Licence</h3>
        <p className="text-sm text-[#14513D]/70 mb-4">Shown as text. Do not use the Government / Ashoka emblem or a recreated Tea Board logo.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {field("licence_authority", "Licensing authority", "Tea Board India")}
          {field("licence_number", "Licence number", "TB|LC|DI-10237")}
        </div>
        <Separator className="my-5" />
        <div className="grid sm:grid-cols-2 gap-6 items-start">
          <FileUpload
            label="Authorised Tea Board logo (optional)"
            accept=".png,.jpg,.jpeg,.webp,.svg" purpose="teaboard-logo" testid="teaboard-logo" heightClass="h-40"
            value={b.teaboard_logo_file_id ? { id: b.teaboard_logo_file_id, url: b.teaboard_logo_url } : null}
            onChange={(f) => setB((x) => ({
              ...x,
              teaboard_logo_file_id: f?.id || null,
              teaboard_logo_url: f?.url || null,
              teaboard_logo_upload_date: f ? new Date().toISOString() : null,
              teaboard_logo_confirmed: f ? x.teaboard_logo_confirmed : false,
            }))}
          />
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              Upload only an <b>authorised</b> Tea Board logo asset that you have permission to use. Never upload the
              Government of India / National / Ashoka emblem or an AI-generated or recreated Tea Board logo. The logo
              appears on catalogues <b>only</b> after you confirm permission below — otherwise the licence stays text-only.
            </div>
            <label className="flex items-start gap-3">
              <Switch
                checked={!!b.teaboard_logo_confirmed}
                disabled={!b.teaboard_logo_file_id}
                onCheckedChange={(v) => set("teaboard_logo_confirmed", v)}
                data-testid="teaboard-confirm"
              />
              <span className="text-sm font-medium text-[#14513D]">
                I confirm I am authorised to display this Tea Board logo on customer catalogues.
              </span>
            </label>
            {b.teaboard_logo_file_id && !b.teaboard_logo_confirmed && (
              <div className="text-xs text-[#14513D]/60">Logo uploaded but hidden until you confirm permission.</div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-display text-2xl text-[#0B3B2E] mb-4">Google Rating</h3>
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label>Rating</Label>
            <Input value={b.google_rating || ""} onChange={(e) => set("google_rating", e.target.value)} data-testid="brand-google_rating" />
          </div>
          <div className="space-y-1.5">
            <Label>Last verified date</Label>
            <Input type="date" value={b.google_rating_verified_date || ""} onChange={(e) => set("google_rating_verified_date", e.target.value)} data-testid="brand-verified-date" />
          </div>
          <label className="flex items-center gap-3 pb-2">
            <Switch checked={b.show_google_rating !== false} onCheckedChange={(v) => set("show_google_rating", v)} data-testid="brand-show-rating" />
            <span className="text-sm font-medium text-[#14513D]">Show on cover</span>
          </label>
        </div>
        <div className="mt-3 flex items-center gap-1 text-[#C9A34A]">
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-5 w-5 fill-[#C9A34A]" />)}
          <span className="ml-2 text-sm text-[#14513D]">{b.google_rating}</span>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-display text-2xl text-[#0B3B2E] mb-1">B2B Marketplace Presence</h3>
        <p className="text-sm text-[#14513D]/70 mb-4">Upload authentic logos sourced from each official website. Enable/disable each platform separately.</p>
        <div className="space-y-4">
          {(b.marketplaces || []).map((m, i) => (
            <div key={m.name} className="rounded-lg border border-[#C9A34A]/25 p-4" data-testid={`mkt-${m.name}`}>
              <div className="flex flex-wrap items-center gap-4">
                <div className="w-32 font-semibold text-[#0B3B2E]">{m.name}</div>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={m.enabled} onCheckedChange={(v) => setMkt(i, { enabled: v })} data-testid={`mkt-enable-${m.name}`} />
                  {m.enabled ? "Enabled" : "Disabled"}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!m.verified_supplier} onCheckedChange={(v) => setMkt(i, { verified_supplier: v })} data-testid={`mkt-verified-${m.name}`} />
                  <ShieldCheck className="h-4 w-4 text-[#C9A34A]" /> Verified Supplier
                </label>
                <a href={m.source_url} target="_blank" rel="noreferrer" className="text-xs text-sky-700 underline ml-auto">{m.source_url}</a>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-4">
                <FileUpload
                  label="Official logo (SVG/PNG)" accept=".png,.jpg,.jpeg,.webp,.svg" purpose="mkt-logo" testid={`mkt-logo-${m.name}`} heightClass="h-24"
                  value={m.logo_file_id ? { id: m.logo_file_id, url: m.logo_url } : null}
                  onChange={(f) => setMkt(i, { logo_file_id: f?.id || null, logo_url: f?.url || null, upload_date: f ? new Date().toISOString() : null })}
                />
                <div className="text-xs text-[#14513D]/70 self-center">
                  {m.logo_file_id ? `Logo uploaded${m.upload_date ? " · " + new Date(m.upload_date).toLocaleDateString() : ""}` : "No logo uploaded — a text badge will be shown. Store the approved asset locally here."}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-display text-2xl text-[#0B3B2E] mb-3">Credential Strip Preview</h3>
        <div className="max-w-lg rounded-lg overflow-hidden border border-[#C9A34A]/30" style={{ fontSize: "18px" }}>
          <CredentialStrip brand={b} />
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Database className="h-5 w-5 text-[#0B3B2E]" />
          <h3 className="font-display text-2xl text-[#0B3B2E]">Data Management</h3>
        </div>
        <p className="text-sm text-[#14513D]/70 mb-4">
          Populate the workspace with sample catalogues, or reset it. Users, brand identity and uploaded logos are always preserved.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={seedDemo} disabled={seeding} data-testid="seed-demo" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2">
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Create demo data
          </Button>
          <Button variant="outline" onClick={() => wipe("demo")} disabled={wiping} data-testid="wipe-demo" className="gap-2">
            <RefreshCw className="h-4 w-4" /> Remove demo data only
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={wiping} data-testid="wipe-all-open" className="gap-2 text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="h-4 w-4" /> Wipe all data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Wipe all catalogue data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes every catalogue, the entire product library, saved covers and version history, and
                  resets catalogue numbering to start fresh. Users, brand settings and uploaded logos are kept. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => wipe("all")} className="bg-red-600 hover:bg-red-700" data-testid="wipe-all-confirm">
                  Wipe everything
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>
    </div>
  );
}
